// src/contexts/NavigationStackContext.jsx
import React, { createContext, useContext, useCallback, useRef, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const NavigationStackContext = createContext(null);

const MAX_STACK_SIZE = 20;
const DEBUG = process.env.REACT_APP_DEBUG === 'true';

// Paths where duplicate entries for the exact same URL are removed
const REPLACE_PREVIOUS_PATHS = [
  '/tasks/',
];

const log = (...args) => {
  if (DEBUG) {
    console.log('[NavigationStack]', ...args);
  }
};

const logStack = (stack, label = 'Current stack') => {
  if (DEBUG) {
    console.log(`[NavigationStack] ${label}:`, [...stack]);
    for (let i = 0; i < stack.length; i++) {
      console.log(`[NavigationStack] #${i} - ${stack[i]}`)
    }
  }
};

const shouldReplacePrevious = (path) => {
  return REPLACE_PREVIOUS_PATHS.some(replacePath => path.startsWith(replacePath));
};

export function NavigationStackProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const stackRef = useRef([]);
  const [stackSnapshot, setStackSnapshot] = useState([]);
  const previousPathRef = useRef(null);
  const skipNextPushRef = useRef(false);

  useEffect(() => {
    log('Provider initialized');
    logStack(stackRef.current, 'Initial stack');
  }, []);

  // Track navigation and update stack
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    const currentPathname = location.pathname;

    log('Location changed:', currentPath);
    log('Previous path:', previousPathRef.current);

    // Check if we should skip this push
    if (skipNextPushRef.current) {
      log('Skipping stack push - navigateSilent was used');
      skipNextPushRef.current = false;
      previousPathRef.current = currentPath;
      return;
    }

    // Skip if same as previous
    if (currentPath === previousPathRef.current) {
      log('Skipping - same as previous path');
      return;
    }

    const lastInStack = stackRef.current[stackRef.current.length - 1];
    const lastPathname = lastInStack ? lastInStack.split('?')[0] : null;

    log('Last in stack:', lastInStack);
    log('Last pathname:', lastPathname);

    // Check if only search params changed on the same pathname
    if (lastPathname === currentPathname && stackRef.current.length > 0) {
      log('Same pathname, updating search params in stack');
      stackRef.current[stackRef.current.length - 1] = currentPath;
      setStackSnapshot([...stackRef.current]);
      logStack(stackRef.current, 'Stack after update');
    } else {
      // Remove previous entries with the exact same pathname (dedup same page only)
      if (shouldReplacePrevious(currentPathname)) {
        const filteredStack = stackRef.current.filter(path => path.split('?')[0] !== currentPathname);
        const removedCount = stackRef.current.length - filteredStack.length;
        if (removedCount > 0) {
          log(`Removed ${removedCount} previous entries for same pathname: ${currentPathname}`);
          stackRef.current = filteredStack;
        }
      }

      // Avoid consecutive duplicates
      if (lastInStack !== currentPath) {
        stackRef.current.push(currentPath);
        log('Pushed to stack:', currentPath);

        if (stackRef.current.length > MAX_STACK_SIZE) {
          const removed = stackRef.current.shift();
          log('Stack limit reached, removed oldest:', removed);
        }

        setStackSnapshot([...stackRef.current]);
        logStack(stackRef.current, 'Stack after push');
      } else {
        log('Skipping - would create consecutive duplicate');
      }
    }

    previousPathRef.current = currentPath;
  }, [location.pathname, location.search]);

  const navigateBack = useCallback((fallbackPath) => {
    const currentPath = location.pathname + location.search;
    
    log('navigateBack called');
    log('Current path:', currentPath);
    log('Fallback path:', fallbackPath);
    logStack(stackRef.current, 'Stack before navigation');
  
    let previousPath;
    
    // Remove current path from stack if present
    if (stackRef.current[stackRef.current.length - 1] === currentPath) {
      stackRef.current.pop();
      setStackSnapshot([...stackRef.current]);
      log('Removed current path from stack');
    }
    
    // Get the previous path (don't pop, just read)
    previousPath = stackRef.current[stackRef.current.length - 1];
    log('Previous path from stack:', previousPath);
  
    if (previousPath && previousPath !== currentPath) {
      log('Navigating to previous path:', previousPath);
      logStack(stackRef.current, 'Stack after navigation');
      skipNextPushRef.current = true;
      navigate(previousPath);
    } else if (fallbackPath) {
      log('No previous path, using fallback:', fallbackPath);
      logStack(stackRef.current, 'Stack after navigation');
      navigate(fallbackPath);
    } else {
      log('No previous path or fallback, using browser back');
      logStack(stackRef.current, 'Stack after navigation');
      navigate(-1);
    }
  }, [navigate, location.pathname, location.search]);

  const navigateSilent = useCallback((path, options) => {
    log('navigateSilent called');
    log('Target path:', path);
    log('Will skip adding to stack');
    
    skipNextPushRef.current = true;
    navigate(path, options);
  }, [navigate]);

  const clearStack = useCallback(() => {
    log('clearStack called');
    logStack(stackRef.current, 'Stack before clear');
    stackRef.current = [];
    setStackSnapshot([]);
    log('Stack cleared');
  }, []);

  const getStack = useCallback(() => {
    return [...stackRef.current];
  }, []);

  const getStackSize = useCallback(() => {
    return stackRef.current.length;
  }, []);

  const popToPath = useCallback((pathname) => {
    const index = stackRef.current.findLastIndex(
      entry => entry.split('?')[0] === pathname
    );
    if (index !== -1) {
      stackRef.current = stackRef.current.slice(0, index + 1);
      setStackSnapshot([...stackRef.current]);
      log('popToPath:', pathname, '→ stack trimmed to index', index);
    }
  }, []);

  const value = {
    navigateBack,
    navigateSilent,
    clearStack,
    popToPath,
    getStack,
    getStackSize,
    stack: stackSnapshot,
  };

  return (
    <NavigationStackContext.Provider value={value}>
      {children}
    </NavigationStackContext.Provider>
  );
}

export function useNavigationStack() {
  const context = useContext(NavigationStackContext);
  
  if (!context) {
    throw new Error('useNavigationStack must be used within NavigationStackProvider');
  }
  
  return context;
}